import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wrench, Clock, Moon, Sun, LogIn } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/hooks/useTheme';
import { api } from '@/api/apiClient';

// Get API URL from environment variable
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function MaintenanceMode() {
  const { isDark, toggleTheme } = useTheme();
  const [message, setMessage] = useState('We are currently performing scheduled maintenance. We will be back shortly.');
  const [endTime, setEndTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    loadMaintenanceInfo();
    const interval = setInterval(checkMaintenanceStatus, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!endTime) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      const remaining = end - now;

      if (remaining <= 0) {
        setTimeRemaining(null);
        // Check if maintenance is still active
        checkMaintenanceStatus();
        return;
      }

      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

      setTimeRemaining({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [endTime]);

  const loadMaintenanceInfo = async () => {
    try {
      // Use public endpoint to load maintenance info (no auth required)
      const response = await fetch(`${API_URL}/api/public/maintenance-status`);
      const data = await response.json();

      if (data.message) {
        setMessage(data.message);
      }

      if (data.end_time) {
        setEndTime(data.end_time);
      }
    } catch (err) {
      console.error('Failed to load maintenance info:', err);
    }
  };

  const checkMaintenanceStatus = async () => {
    try {
      // Use public endpoint to check status (no auth required)
      const response = await fetch(`${API_URL}/api/public/maintenance-status`);
      const data = await response.json();
      
      // If maintenance mode is off, reload the page
      if (data.maintenance_mode === false) {
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Failed to check maintenance status:', err);
    }
  };

  const handleAdminLogin = () => {
    // Redirect to login with a return URL
    api.auth.redirectToLogin();
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300 flex flex-col">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl w-full"
        >
          <Card className="border-2 shadow-xl">
            <CardContent className="p-12 text-center space-y-6">
              {/* Animated Icon */}
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center mx-auto"
              >
                <Wrench className="w-12 h-12 text-white" />
              </motion.div>

              {/* Title */}
              <div>
                <h1 className="text-4xl font-bold text-foreground mb-3">
                  Under Maintenance
                </h1>
                <p className="text-xl text-muted-foreground">
                  {message}
                </p>
              </div>

              {/* Countdown Timer */}
              {timeRemaining && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-muted rounded-lg p-6 border border-border"
                >
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Clock className="w-5 h-5 text-orange-500" />
                    <p className="text-sm font-medium text-muted-foreground">
                      Estimated time remaining
                    </p>
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-foreground">
                        {String(timeRemaining.hours).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">Hours</div>
                    </div>
                    <div className="text-4xl font-bold text-muted-foreground">:</div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-foreground">
                        {String(timeRemaining.minutes).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">Minutes</div>
                    </div>
                    <div className="text-4xl font-bold text-muted-foreground">:</div>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-foreground">
                        {String(timeRemaining.seconds).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">Seconds</div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Admin Login */}
              <div className="pt-6 border-t border-border">
                <p className="text-sm text-muted-foreground mb-4">
                  Are you an administrator?
                </p>
                <Button
                  onClick={handleAdminLogin}
                  variant="outline"
                  className="gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Admin Login
                </Button>
              </div>

              {/* Additional Info */}
              <div className="pt-4">
                <p className="text-sm text-muted-foreground">
                  We apologize for any inconvenience. Please check back soon.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-muted border-t border-border py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground text-sm">© 2025 BenefitsPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
